import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faWarning } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { ListPerson } from "../../../schema/list";
import { isLeadUnsubscribed } from "../../../utils/isLeadBlacklisted";

interface BlockedLeadBannerProps {
	/** The lead the panel is showing; the banner names why *this* one may not be contacted. */
	listPerson?: ListPerson | null;
}

/**
 * Says why a lead is not simply written to (PREC-3728). Blacklisted and unsubscribed are different
 * promises to the reader — one is our decision, the other theirs — so the banner names which.
 *
 * Since PREC-2548 they also lead to different panels. An unsubscribed lead has no chat toggle and
 * no reach-out button, and the banner is the only explanation. A blacklisted lead keeps its
 * reach-out button, behind the blacklist override modal, and the banner says that contacting it
 * asks first and takes it off the blacklist.
 */
export const BlockedLeadBanner: FunctionComponent<BlockedLeadBannerProps> = ({ listPerson }) => {
	const { t } = useTranslation();

	return (
		<div
			className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-status-error/30 bg-status-error-light px-3 py-2 text-xs text-status-error"
			data-testid="blocked-lead-banner"
		>
			<Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" icon={faWarning} />
			<span>
				{isLeadUnsubscribed(listPerson)
					? t("inbox.chatDisabled.unsubscribed")
					: t("inbox.chatDisabled.blacklisted")}
			</span>
		</div>
	);
};
